import type { ContentRef, DocumentTableImageRecord } from '@gitbook/api-client';
import { type AncestorNode, Block, Fragment, generateKey, Text, type Value } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';
import { compareOrderIndexes, getRandomLexoRank, type LexoRank } from '@gitbook/util-orderindex';
import { assert, assertIsDefined } from '@gitbook/util-ts';

import {
    BLOCK_PARAGRAPH,
    BLOCK_TABLE,
    type BlockTableData,
    FRAGMENT_TABLE_CELL,
    type TableColumnDefinition,
    type TableColumnType,
    type TableRecord,
    type TableViewType,
} from '../types';
import { columnSpecs } from './columns';

/**
 * Returns a sorted list of Record keys based on table data.
 */
export function defaultRecordSortedKeys(data: BlockTableData): string[] {
    return Object.keys(data.records).sort((a, b) => {
        return compareOrderIndexes(data.records[a], data.records[b]);
    });
}

/**
 * Get last order index in the table, to insert a record after.
 */
export function getLastRecordOrderIndex(data: BlockTableData): string {
    const orderedRecords = Object.values(data.records).sort(compareOrderIndexes);
    return orderedRecords[orderedRecords.length - 1].orderIndex;
}

/**
 * Get last column index in the table, to insert a column after.
 */
export function getLastColumnIndex(data: BlockTableData): number {
    return data.view.columns.length - 1;
}

/*
 * Create an empty table.
 */
export function createTable(
    options: { rows?: number; columns?: number; view?: TableViewType } = {}
): Block {
    const { rows = 3, columns = 3, view = 'grid' } = options;
    // We don't necessarily normalize the table component, so we make sure to create one
    // with valid data when creating it.
    const blockDataView: BlockTableData['view'] =
        view === 'grid'
            ? {
                  type: 'grid',
                  columns: [],
                  hideHeader: false,
                  stickyHeader: false,
                  useNewSizing: true,
              }
            : {
                  type: 'cards',
                  cardSize: 'medium',
                  hideColumnTitle: false,
                  columns: [],
              };

    const data: BlockTableData = {
        records: {},
        definition: {},
        view: blockDataView,
    };

    let rank: LexoRank | undefined;
    for (let row = 0; row < rows; row++) {
        const recordId = generateKey();
        rank = rank?.genNext() ?? getRandomLexoRank();
        data.records[recordId] = {
            values: {},
            orderIndex: rank.toString(),
        };
    }

    for (let column = 0; column < columns; column++) {
        const columnId = generateKey();
        data.definition[columnId] = {
            id: columnId,
            title: '',
            type: 'text',
            textAlignment: 'left',
        };

        data.view.columns?.push(columnId);
    }

    return Block.createVoid({
        type: BLOCK_TABLE,
        data,
        fragments: [],
    });
}

/*
 * Create a table from data.
 */
export function createTableFromData(
    dataColumns: Array<{
        title?: string;
    }>,
    dataRows: Array<
        Array<{
            value: string | number | boolean | Fragment | Block[];
        }>
    >,
    options: {
        rows?: number;
        columns?: number;
        view?: TableViewType;
    } = {}
): Block {
    const { view = 'grid' } = options;
    // We don't necessarily normalize the table component, so we make sure to create one
    // with valid data when creating it.
    const blockDataView: BlockTableData['view'] =
        view === 'grid'
            ? {
                  type: 'grid',
                  columns: [],
                  hideHeader: false,
                  stickyHeader: false,
                  useNewSizing: true,
              }
            : {
                  type: 'cards',
                  cardSize: 'medium',
                  hideColumnTitle: false,
                  columns: [],
              };

    const fragments: Fragment[] = [];

    const data: BlockTableData = {
        records: {},
        definition: {},
        view: blockDataView,
    };

    for (let column = 0; column < dataRows[0].length; column++) {
        const value = dataRows[0][column].value;

        const columnId = generateKey();

        switch (typeof value) {
            case 'number': {
                data.definition[columnId] = {
                    id: columnId,
                    title: dataColumns[column].title ?? '',
                    type: 'number',
                };
                break;
            }
            case 'boolean': {
                data.definition[columnId] = {
                    id: columnId,
                    title: dataColumns[column].title ?? '',
                    type: 'checkbox',
                };
                break;
            }
            default: {
                data.definition[columnId] = {
                    id: columnId,
                    title: dataColumns[column].title ?? '',
                    type: 'text',
                    textAlignment: 'left',
                };
                break;
            }
        }

        data.view.columns?.push(columnId);
    }

    let rank: LexoRank | undefined;
    for (let row = 0; row < dataRows.length; row++) {
        const recordId = generateKey();
        rank = rank?.genNext() ?? getRandomLexoRank();
        data.records[recordId] = {
            values: {},
            orderIndex: rank.toString(),
        };

        for (let column = 0; column < dataRows[row].length; column++) {
            const value = dataRows[row][column].value;

            const columnId = data.view.columns?.[column];
            assertIsDefined(columnId);

            switch (typeof value) {
                case 'boolean':
                case 'number': {
                    data.records[recordId].values[columnId] = value;
                    break;
                }
                default: {
                    const columnValue = generateKey();
                    const fragment = Fragment.create({
                        // We generate a unique local fragment key for each cell to ensure it stays linked to the record data
                        // when copy/pasting and regenerating keys.
                        fragment: columnValue,
                        type: FRAGMENT_TABLE_CELL,
                        nodes: Fragment.isFragment(value)
                            ? value.nodes
                            : Array.isArray(value)
                              ? value
                              : [
                                    Block.create({
                                        type: BLOCK_PARAGRAPH,
                                        nodes: [
                                            Text.create({
                                                text: value.toString(),
                                            }),
                                        ],
                                    }),
                                ],
                    });

                    fragments.push(fragment);
                    data.records[recordId].values[columnId] = columnValue;
                }
            }
        }
    }

    return Block.createVoid({
        type: BLOCK_TABLE,
        data,
        fragments,
    });
}

/**
 * Create a basic column from a type.
 */
export function createColumnFromType(type: TableColumnType, title = ''): TableColumnDefinition {
    return normalizeColumnDefinition({
        id: generateKey(),
        title,
        type,
    });
}

/**
 * Change an existing column type.
 */
export function changeColumnType(
    def: Pick<TableColumnDefinition, 'id' | 'title'>,
    type: TableColumnType
): TableColumnDefinition {
    return normalizeColumnDefinition({
        id: def.id,
        title: def.title,
        type,
    });
}

/**
 * Normalize a column definition.
 */
export function normalizeColumnDefinition(
    colDef: Partial<TableColumnDefinition> & Pick<TableColumnDefinition, 'id' | 'title' | 'type'>
): TableColumnDefinition {
    const spec = columnSpecs[colDef.type];
    // @ts-ignore the `normalizeDefinition` takes care of adding what's needed if not there
    return spec.normalizeDefinition ? spec.normalizeDefinition(colDef) || colDef : colDef;
}

/**
 * Get the current table being edited and the position in the table if it's editing text.
 */
export function getCurrentTable(value: Value): {
    table: Block;
    fragment?: Fragment;

    /**
     * When selection is in a leaf block, all ancestors in the table for this leaf block.
     * It doesn't include the fragment.
     *
     * For example, if selection is in:
     *  - table > fragment > list-ul > list-item > paragraph
     *      => ancestorBlocks will be [list-url, list-item]
     *
     *  - table > fragment > paragraph
     *      => ancestorBlocks will be []
     */
    ancestorBlocks?: List<AncestorNode>;
} | null {
    const { document, startBlock } = value;

    if (!startBlock) {
        return null;
    }

    if (startBlock.type === BLOCK_TABLE) {
        return { table: startBlock };
    }

    const ancestors = document.getAncestors(startBlock.key);
    if (!ancestors) {
        return null;
    }

    const tableEntry = ancestors.findLastEntry(
        (node) => node.object === 'block' && node.type === BLOCK_TABLE
    );
    if (!tableEntry) {
        return null;
    }

    const fragment = ancestors.get(tableEntry[0] + 1);
    const ancestorBlocks = ancestors.slice(tableEntry[0] + 2);

    return {
        table: tableEntry[1] as Block,
        fragment: fragment?.object === 'fragment' ? fragment : undefined,
        ancestorBlocks,
    };
}

/**
 * Return true if a record is empty.
 */
export function isRowEmpty(table: Block, recordId: string): boolean {
    const data = getTableData(table);

    const record = data.records[recordId];

    return Object.values(data.definition).every((definition) => {
        const recordValue = record.values[definition.id];

        if (definition.type === 'text') {
            const fragment = table.getFragmentByKey(String(recordValue));
            return fragment ? fragment.isEmpty : true;
        }

        if (Array.isArray(recordValue)) {
            return recordValue.length === 0;
        }

        return !recordValue;
    });
}

/**
 * Is the table header empty.
 */
export function isHeaderEmpty(table: Block | BlockTableData): boolean {
    const data = getTableData(table);

    return Object.values(data.definition).every((definition) => {
        return !definition.title;
    });
}

/**
 * Utility to allow utils functions to accept both data or a block.
 */
export function getTableData(table: Block | BlockTableData): BlockTableData {
    if (Block.isBlock(table)) {
        return table.data.toFrozenObject<BlockTableData>() as BlockTableData;
    }

    return table;
}

/**
 * Returns an array of stringified values for each cell in the row.
 */
export function getTableRowPlainTexts(node: Block, rowRecord: TableRecord): string[] {
    const tableData = getTableData(node);
    const definition = tableData.definition;

    return tableData.view.columns.map((column) => {
        const def = definition[column];
        assert(def !== undefined);

        const value = rowRecord.values[def.id];
        if (def.type === 'text') {
            return node.fragments.get(String(value))?.text ?? '';
        }
        if (def.type === 'number') {
            return value !== null ? value.toString() : '';
        }

        return '';
    });
}

type TableDefinitionValues = {
    text: string;
    number: number;
    checkbox: boolean;
    files: string[];
    users: string[];
    rating: number | undefined | null;
    select: string[];
    'content-ref': ContentRef | null;
    image: DocumentTableImageRecord | undefined | null;
};

type TableDefinitionValue<Def extends TableColumnDefinition> = TableDefinitionValues[Def['type']];

/**
 * Get the value for a definition in a record.
 * This function should be used to get the value for a definition in a record as it
 * we want to ensure that the value is always typed.
 */
export function getTableRecordValue<Def extends TableColumnDefinition>(
    record: TableRecord,
    definition: Def
): TableDefinitionValue<Def> {
    // @ts-expect-error - values is type as anything
    return record.values[definition.id];
}
